import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const applyAuth = useCallback((authToken, authUser) => {
    if (!authToken) {
      clearAuth();
      return;
    }
    localStorage.setItem('adminToken', authToken);
    if (authUser) {
      localStorage.setItem('adminUser', JSON.stringify(authUser));
      setUser(authUser);
    }
    setToken(authToken);
    setIsAuthenticated(true);
  }, [clearAuth]);

  const login = useCallback((authToken, authUser = null) => {
    applyAuth(authToken, authUser);
  }, [applyAuth]);

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const hasSection = useCallback(
    (section) => {
      if (!user) return false;
      if (user.is_bootstrap_admin) return true;
      const sections = user.sections || [];
      return sections.includes(section);
    },
    [user]
  );

  const firstAllowedPath = useCallback(() => {
    const order = [
      ['dashboard', '/admin'],
      ['inventory', '/admin/inventory'],
      ['deals', '/admin/deals'],
      ['documents', '/admin/documents'],
      ['operations', '/admin/operations'],
      ['leads', '/admin/leads'],
      ['service', '/admin/service-requests'],
      ['imagine', '/admin/imagine'],
      ['outreach', '/admin/outreach'],
      ['schedule', '/admin/schedule'],
      ['user_management', '/admin/users'],
    ];
    for (const [section, path] of order) {
      if (hasSection(section)) return path;
    }
    return '/admin/login';
  }, [hasSection]);

  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setToken(storedToken);
    // Optimistic: show cached user while validating
    try {
      const cached = localStorage.getItem('adminUser');
      if (cached) setUser(JSON.parse(cached));
    } catch {
      /* ignore */
    }

    axios
      .get(`${BACKEND_URL}/api/admin/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      })
      .then((res) => {
        if (cancelled) return;
        const me = res.data?.user;
        applyAuth(storedToken, me);
      })
      .catch(() => {
        if (!cancelled) clearAuth();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyAuth, clearAuth]);

  const value = {
    isAuthenticated,
    token,
    user,
    loading,
    login,
    logout,
    hasSection,
    firstAllowedPath,
    canRevealNpi: Boolean(user?.can_reveal_npi || user?.is_bootstrap_admin),
    isDeveloper: Boolean(user?.is_developer) || [user?.custom_role_label, user?.role_label].some(
      (label) => String(label || '').trim().toLowerCase() === 'developer'
    ),
    setUser: (next) => {
      setUser(next);
      if (next) {
        localStorage.setItem('adminUser', JSON.stringify(next));
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
